import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext, SttList } from '../../../sporttia/all';

type MshipData = {
    id: number;
};

type Props = {
    mship: MshipData;
};

type PrivilegeData = {
    id?: number;
    name?: string;
    description?: string;
    code?: string;
    group?: {
        id?: number;
        name?: string;
    } | null;
};

type PrivilegesResponse = {
    privileges: PrivilegeData[];
};

export default function MshipPrivileges({ mship }: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const [privileges, setPrivileges] = useState<PrivilegeData[]>([]);
    const { t } = useTranslation();

    /**
     * Get privileges
     */
    const loadPrivileges = (): void => {
        cxt?.api('GET', `/mships/${mship.id}/privileges`, {
            params: {
                page: 1,
                rows: 100,
            },
            success: (r: PrivilegesResponse) => {
                setPrivileges(r.privileges);
            },
        });
    };

    /**
     * Init
     */
    useEffect(() => {
        loadPrivileges();
    }, []);

    // Helper function to extract privilege code from potentially full translation key
    const extractPrivilegeCode = (code: string): string => {
        let privilegeCode = code;
        // If the code already contains the full translation key path, extract just the privilege code
        // e.g., "hart.privilege.accounting.name" -> "accounting"
        if (privilegeCode.startsWith('hart.privilege.')) {
            privilegeCode = privilegeCode
                .replace('hart.privilege.', '')
                .replace('.name', '')
                .replace('.desc', '');
        }
        return privilegeCode.toLowerCase();
    };

    // Helper function to get translated privilege name
    const getPrivilegeTranslation = (code: string): string => {
        const normalizedKey = extractPrivilegeCode(code);
        return t(`hart.privilege.${normalizedKey}.name`, {
            defaultValue: code,
        });
    };

    // Helper function to get translated privilege description
    const getPrivilegeDescription = (code: string): string => {
        const normalizedKey = extractPrivilegeCode(code);
        return t(`hart.privilege.${normalizedKey}.desc`, {
            defaultValue: '',
        });
    };

    // -----| Render |-----
    return (
        <SttList
            data={
                (privileges &&
                    privileges.map((priv: PrivilegeData) => {
                        // Use code field to construct translation key path
                        const privilegeCode = priv.code || priv.name || '';

                        return {
                            caption: privilegeCode
                                ? getPrivilegeTranslation(privilegeCode)
                                : '',
                            text: privilegeCode
                                ? getPrivilegeDescription(privilegeCode)
                                : '',
                            chips: priv?.group?.name
                                ? [{ caption: priv.group.name }]
                                : null,
                        };
                    })) as any
            }
        />
    );
}
